// Top-level gate + mount point for the QA Bug-Hunt companion.
// - Reads qaMode from settingsStore and qa_tester from authStore
// - Renders nothing unless both are true (global admin bypasses qa_tester)
// - Registers Ctrl/Cmd+Shift+B to toggle the drawer
// - Auto-captures context (URL, role, site IDs, viewport, user agent)

import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useSettingsStore } from '../../store/settingsStore'
import useAuthStore from '../../store/authStore'
import { useSiteFilterStore } from '../../store/siteFilterStore'
import { matchRouteName } from '../../config/routeNames'
import QAFab from './QAFab'
import QABugDrawer from './QABugDrawer'
import './qa.css'

interface SubmitResult {
  ok: boolean
  message: string
}

function useAutoContext() {
  const location = useLocation()
  const { user } = useAuthStore()
  const siteIds = useSiteFilterStore(s => s.siteIds)

  return {
    url: location.pathname + (location.search || ''),
    route_name: matchRouteName(location.pathname) as string | null,
    user_role: user?.role || 'unknown',
    site_id: siteIds.length ? parseInt(siteIds[0], 10) || null : null,
    site_name: null as string | null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    user_agent: navigator.userAgent,
  }
}

export default function QAOverlay() {
  const qaMode = useSettingsStore(s => s.qaMode)
  const user = useAuthStore(s => s.user)
  const [open, setOpen] = useState(false)
  const context = useAutoContext()

  const canShow = qaMode && (user?.is_global_admin || user?.qa_tester)

  useEffect(() => {
    if (!canShow) return
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault()
        setOpen(o => !o)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [canShow])

  if (!canShow) return null

  function handleSubmitted({ ok, message }: SubmitResult) {
    try {
      window.dispatchEvent(new CustomEvent('stagewise:toast', { detail: { type: ok ? 'success' : 'info', message } }))
    } catch { /* no-op */ }
  }

  return (
    <>
      {!open && <QAFab onOpen={() => setOpen(true)} />}
      {open && (
        <QABugDrawer
          context={context}
          onClose={() => setOpen(false)}
          onSubmitted={handleSubmitted}
        />
      )}
    </>
  )
}
